import { REACTOR_STORM_RULES } from "../../config/gameplay";
import { clamp } from "../../utils/math";
import { rngUnit } from "../../utils/rng";
import { dailyIgnitionMultiplier } from "../daily-ignition";
import { chooseCoreColor } from "../cores/factory";
import type { ProfileProgress } from "../progression/levels";
import type { OrbitForgeSave } from "../save/types";
import type { CoreColor, Vector2 } from "../runtime/types";
import type { UpgradeEffects } from "../upgrades/effects";
import type { FeedbackDraft } from "../runtime/feedback";
import type { OverdriveView } from "./overdrive";

export interface ReactorStormBoardState {
  readonly id: number;
  readonly position: Vector2;
  readonly rotationRadians: number;
  readonly multiplier: number;
  readonly color: CoreColor;
}

export interface ReactorStormCoreState {
  readonly id: number;
  position: Vector2;
  velocity: Vector2;
  spinRadians: number;
  readonly color: CoreColor;
  readonly radius: number;
  readonly isOvercharged: boolean;
  readonly rewardMultiplier: number;
  stormMultiplier: number;
  bounceCount: number;
  ageSeconds: number;
  readonly hitBoardIds: number[];
}

export interface ReactorStormRuntimeState {
  elapsedSeconds: number;
  spawnBudget: number;
  spawned: number;
  scored: number;
  totalCores: number;
  totalReward: number;
  readonly boards: readonly ReactorStormBoardState[];
  cores: readonly ReactorStormCoreState[];
}

export interface ReactorStormView {
  readonly unlocked: boolean;
  readonly active: boolean;
  readonly ready: boolean;
  readonly cooldownSeconds: number;
  readonly blockedByOverdrive: boolean;
  readonly overdriveLockSeconds: number;
  readonly scored: number;
  readonly totalCores: number;
  readonly totalReward: number;
  readonly stopPending: boolean;
  readonly boards: readonly ReactorStormBoardState[];
  readonly cores: readonly ReactorStormCoreState[];
}

export interface ReactorStormStartResult {
  readonly state: ReactorStormRuntimeState | null;
  readonly dirty: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

export interface ReactorStormAdvanceResult {
  readonly state: ReactorStormRuntimeState | null;
  readonly dirty: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

const boardColors: readonly CoreColor[] = ["cyan", "pink", "lime", "gold"];

const boardLayout: readonly Omit<ReactorStormBoardState, "color">[] = [
  { id: 1, position: { x: -360, y: 280 }, rotationRadians: -0.34, multiplier: 2 },
  { id: 2, position: { x: -45, y: 250 }, rotationRadians: 0.28, multiplier: 2 },
  { id: 3, position: { x: 310, y: 286 }, rotationRadians: -0.2, multiplier: 2 },
  { id: 4, position: { x: -250, y: 120 }, rotationRadians: 0.42, multiplier: 3 },
  { id: 5, position: { x: 112, y: 108 }, rotationRadians: -0.36, multiplier: 3 },
  { id: 6, position: { x: 394, y: 82 }, rotationRadians: 0.28, multiplier: 3 },
  { id: 7, position: { x: -338, y: -70 }, rotationRadians: -0.28, multiplier: 4 },
  { id: 8, position: { x: 40, y: -118 }, rotationRadians: 0.36, multiplier: 4 },
  { id: 9, position: { x: 322, y: -206 }, rotationRadians: -0.42, multiplier: 5 },
  { id: 10, position: { x: -102, y: -312 }, rotationRadians: 0.22, multiplier: 5 }
];

export function buildReactorStormView(
  save: OrbitForgeSave,
  progress: ProfileProgress,
  overdrive: OverdriveView,
  activeStorm: ReactorStormRuntimeState | null,
  stopPending: boolean
): ReactorStormView {
  const unlocked = progress.effectiveLevel >= REACTOR_STORM_RULES.unlockLevel;
  const active = Boolean(activeStorm);
  const overdriveLockSeconds = overdrive.active ? overdrive.activeSeconds : overdrive.cooldownSeconds <= REACTOR_STORM_RULES.overdriveStartLockSeconds ? overdrive.cooldownSeconds : 0;
  const blockedByOverdrive = overdriveLockSeconds > 0;
  return {
    unlocked,
    active,
    ready: unlocked && !active && save.events.reactorStorm.cooldownSeconds <= 0 && !blockedByOverdrive,
    cooldownSeconds: save.events.reactorStorm.cooldownSeconds,
    blockedByOverdrive,
    overdriveLockSeconds,
    scored: activeStorm?.scored ?? 0,
    totalCores: activeStorm?.totalCores ?? stormCoreCount(progress),
    totalReward: activeStorm?.totalReward ?? 0,
    stopPending,
    boards: activeStorm?.boards ?? [],
    cores: activeStorm?.cores ?? []
  };
}

export function advanceReactorStormCooldown(save: OrbitForgeSave, progress: ProfileProgress, deltaSeconds: number): boolean {
  if (progress.effectiveLevel < REACTOR_STORM_RULES.unlockLevel || save.events.reactorStorm.cooldownSeconds <= 0) {
    return false;
  }

  save.events.reactorStorm.cooldownSeconds = Math.max(0, save.events.reactorStorm.cooldownSeconds - deltaSeconds);
  return true;
}

export function startReactorStorm(save: OrbitForgeSave, progress: ProfileProgress, overdrive: OverdriveView): ReactorStormStartResult {
  if (progress.effectiveLevel < REACTOR_STORM_RULES.unlockLevel || save.events.reactorStorm.cooldownSeconds > 0 || overdrive.active || overdrive.cooldownSeconds <= REACTOR_STORM_RULES.overdriveStartLockSeconds) {
    return {
      state: null,
      dirty: false,
      feedback: [{ kind: "denied", target: "storm", frameX: 1690, frameY: 348 }]
    };
  }

  save.events.reactorStorm.cooldownSeconds = REACTOR_STORM_RULES.cooldownSeconds;
  return {
    dirty: true,
    state: {
      elapsedSeconds: 0,
      spawnBudget: 0,
      spawned: 0,
      scored: 0,
      totalCores: stormCoreCount(progress),
      totalReward: 0,
      boards: createStormBoards(),
      cores: []
    },
    feedback: [
      { kind: "pulse", target: "storm" },
      { kind: "floating-text", target: "storm", frameX: 1690, frameY: 348, color: "gold", text: "STORM START" }
    ]
  };
}

export function advanceReactorStorm(save: OrbitForgeSave, state: ReactorStormRuntimeState, effects: UpgradeEffects, progress: ProfileProgress, deltaSeconds: number): ReactorStormAdvanceResult {
  const feedback: FeedbackDraft[] = [];
  state.elapsedSeconds += deltaSeconds;
  spawnStormCores(save, state, effects, progress, deltaSeconds, feedback);
  scoreAdvancedStormCores(save, state, effects, progress, deltaSeconds, feedback);

  if (state.spawned >= state.totalCores && state.cores.length === 0) {
    save.stats.ReactorStormEventsCompleted += 1;
    save.stats.ReactorStormMaxCompletedCores = Math.max(save.stats.ReactorStormMaxCompletedCores, state.totalCores);
    feedback.push({ kind: "pulse", target: "storm" });
    feedback.push({ kind: "floating-text", target: "storm", frameX: 960, frameY: 286, color: "gold", text: "STORM COMPLETE" });
    return { state: null, dirty: true, feedback };
  }

  return { state, dirty: true, feedback };
}

export function stopReactorStormEarly(): ReactorStormAdvanceResult {
  return {
    state: null,
    dirty: true,
    feedback: [{ kind: "floating-text", target: "storm", frameX: 1690, frameY: 348, color: "white", text: "STORM STOPPED" }]
  };
}

function spawnStormCores(save: OrbitForgeSave, state: ReactorStormRuntimeState, effects: UpgradeEffects, progress: ProfileProgress, deltaSeconds: number, feedback: FeedbackDraft[]): void {
  const spawnRate = state.totalCores / REACTOR_STORM_RULES.spawnDurationSeconds;
  state.spawnBudget = Math.min(REACTOR_STORM_RULES.spawnBudgetCap, state.spawnBudget + spawnRate * deltaSeconds);
  const spawnCount = Math.min(
    REACTOR_STORM_RULES.maxSpawnsPerFrame,
    Math.floor(state.spawnBudget),
    state.totalCores - state.spawned
  );

  for (let index = 0; index < spawnCount; index += 1) {
    state.spawnBudget -= 1;
    const core = createStormCore(save, effects, progress, state);
    state.cores = [...state.cores, core];
    state.spawned += 1;
    save.stats.TotalCoresLaunched += 1;
    save.stats.DailyCoresLaunched += 1;
    save.stats.WeeklyCoresLaunched += 1;
    if (core.isOvercharged) {
      save.stats.OverchargedCoresLaunched += 1;
    }
    feedback.push({ kind: "core-spawn", frameX: core.position.x + 960, frameY: core.position.y + 540, color: core.color });
  }
}

function scoreAdvancedStormCores(save: OrbitForgeSave, state: ReactorStormRuntimeState, effects: UpgradeEffects, progress: ProfileProgress, deltaSeconds: number, feedback: FeedbackDraft[]): void {
  const remaining: ReactorStormCoreState[] = [];
  for (const core of state.cores) {
    advanceStormCore(core, state.boards, deltaSeconds);
    if (core.position.y <= REACTOR_STORM_RULES.exitY || core.ageSeconds >= 8) {
      const payout = applyStormReward(save, core, effects, progress);
      state.scored += 1;
      state.totalReward += payout;
      feedback.push({
        kind: "lane-hit",
        frameX: clamp(core.position.x + 960, 350, 1570),
        frameY: 184,
        color: core.isOvercharged ? "gold" : core.color,
        text: `x${Math.max(1, core.stormMultiplier).toFixed(1)} +${Math.floor(payout)}`
      });
    } else {
      remaining.push(core);
    }
  }
  state.cores = remaining;
}

function createStormCore(save: OrbitForgeSave, effects: UpgradeEffects, progress: ProfileProgress, state: ReactorStormRuntimeState): ReactorStormCoreState {
  const colorRoll = rngUnit(save.rngState);
  const xRoll = rngUnit(colorRoll.state);
  const overchargeRoll = rngUnit(xRoll.state);
  const overchargedChance = clamp(0.01 + effects.overchargedChance * 0.65, 0, 0.075);
  const color = chooseCoreColor(colorRoll.value);
  const position = {
    x: (xRoll.value * 2 - 1) * REACTOR_STORM_RULES.fieldHalfWidth * 0.84,
    y: REACTOR_STORM_RULES.spawnY
  };
  const target = state.boards[state.spawned % state.boards.length];
  const isOvercharged = overchargeRoll.value <= overchargedChance;
  const startMultiplier = progress.effectiveLevel >= REACTOR_STORM_RULES.highScaleUnlockLevel ? REACTOR_STORM_RULES.highStartMultiplier : 1;
  save.rngState = overchargeRoll.state;
  const coreId = save.nextEntityId;
  save.nextEntityId += 1;

  return {
    id: coreId,
    position,
    velocity: {
      x: clamp((target.position.x - position.x) * 0.88, -250, 250),
      y: -530 - colorRoll.value * 60
    },
    spinRadians: xRoll.value * Math.PI * 2,
    color,
    radius: REACTOR_STORM_RULES.eventCoreRadius,
    isOvercharged,
    rewardMultiplier: isOvercharged ? REACTOR_STORM_RULES.overchargedMultiplier : 1,
    stormMultiplier: startMultiplier,
    bounceCount: 0,
    ageSeconds: 0,
    hitBoardIds: []
  };
}

function advanceStormCore(core: ReactorStormCoreState, boards: readonly ReactorStormBoardState[], deltaSeconds: number): void {
  core.ageSeconds += deltaSeconds;
  applyFirstHitAssist(core, boards, deltaSeconds);
  core.velocity.y += 72 * deltaSeconds;
  core.position.x += core.velocity.x * deltaSeconds;
  core.position.y += core.velocity.y * deltaSeconds;
  core.spinRadians = (core.spinRadians + (core.velocity.x * 0.016 + core.velocity.y * 0.01) * deltaSeconds) % (Math.PI * 2);

  for (const board of boards) {
    if (core.hitBoardIds.includes(board.id) || !intersectsBoard(core, board)) {
      continue;
    }
    applyBoardHit(core, board);
  }
}

function applyFirstHitAssist(core: ReactorStormCoreState, boards: readonly ReactorStormBoardState[], deltaSeconds: number): void {
  if (core.hitBoardIds.length > 0 || Math.abs(core.position.y) > REACTOR_STORM_RULES.firstHitAssistRangeY) {
    return;
  }

  const nearest = boards.reduce((best, board) => {
    const distance = Math.abs(board.position.y - core.position.y) + Math.abs(board.position.x - core.position.x) * 0.35;
    return distance < best.distance ? { board, distance } : best;
  }, { board: boards[0], distance: Number.POSITIVE_INFINITY });
  const desiredX = clamp((nearest.board.position.x - core.position.x) * 0.82, -REACTOR_STORM_RULES.firstHitAssistMaxSpeed, REACTOR_STORM_RULES.firstHitAssistMaxSpeed);
  core.velocity.x += (desiredX - core.velocity.x) * Math.min(1, REACTOR_STORM_RULES.firstHitAssistStrength * deltaSeconds * 0.01);
}

function intersectsBoard(core: ReactorStormCoreState, board: ReactorStormBoardState): boolean {
  const cos = Math.cos(board.rotationRadians);
  const sin = Math.sin(board.rotationRadians);
  const dx = core.position.x - board.position.x;
  const dy = core.position.y - board.position.y;
  const localX = dx * cos + dy * sin;
  const localY = -dx * sin + dy * cos;
  return Math.abs(localX) <= REACTOR_STORM_RULES.boardLength / 2 + core.radius && Math.abs(localY) <= REACTOR_STORM_RULES.boardThickness / 2 + core.radius;
}

function applyBoardHit(core: ReactorStormCoreState, board: ReactorStormBoardState): void {
  core.hitBoardIds.push(board.id);
  core.bounceCount += 1;
  core.stormMultiplier = Math.min(REACTOR_STORM_RULES.maxMultiplier, core.stormMultiplier * board.multiplier);
  const normal = { x: -Math.sin(board.rotationRadians), y: Math.cos(board.rotationRadians) };
  const dot = core.velocity.x * normal.x + core.velocity.y * normal.y;
  const spread = Math.sin((core.id + board.id * 13) * 12.9898) * (core.isOvercharged ? 18 : 42);
  core.velocity.x = (core.velocity.x - 2 * dot * normal.x) * 0.72 + spread;
  core.velocity.y = Math.min(-145, -Math.abs(core.velocity.y - 2 * dot * normal.y) * (core.isOvercharged ? 0.56 : 0.68) - 110);
}

function applyStormReward(save: OrbitForgeSave, core: ReactorStormCoreState, effects: UpgradeEffects, progress: ProfileProgress): number {
  const scale = progress.effectiveLevel >= REACTOR_STORM_RULES.highScaleUnlockLevel ? REACTOR_STORM_RULES.highRewardScale : REACTOR_STORM_RULES.rewardScale;
  const baseValue = effects.coreBaseValue + core.bounceCount * effects.chargeValue;
  const multiplier = Math.max(1, core.stormMultiplier) * Math.max(1, core.rewardMultiplier) * scale * dailyIgnitionMultiplier(save);
  const payout = Math.ceil(baseValue * multiplier);
  save.stats.Credits += payout;
  save.stats.TotalEarnedCredits += payout;
  save.stats.DailyEarnedCredits += payout;
  save.stats.WeeklyEarnedCredits += payout;
  save.stats.TotalCoresCollected += 1;
  save.stats.DailyCoresCollected += 1;
  save.stats.WeeklyCoresCollected += 1;
  save.stats.BestMultiplier = Math.max(save.stats.BestMultiplier, Math.ceil(multiplier));
  return payout;
}

function createStormBoards(): readonly ReactorStormBoardState[] {
  return boardLayout.map((board, index) => ({
    ...board,
    color: boardColors[index % boardColors.length]
  }));
}

function stormCoreCount(progress: ProfileProgress): number {
  if (progress.prestigeLevel > 0) {
    return REACTOR_STORM_RULES.prestigeCoreCount;
  }
  if (progress.effectiveLevel >= 30) {
    return REACTOR_STORM_RULES.level30CoreCount;
  }
  if (progress.effectiveLevel >= 10) {
    return REACTOR_STORM_RULES.level10CoreCount;
  }
  return REACTOR_STORM_RULES.baseCoreCount;
}
